import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AvatarCustomizer from "../components/AvatarCustomizer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  MessageSquare, Send, Bot, User, TrendingUp, Target, 
  Bell, Award, BookOpen, Zap, ChevronRight, Settings, Dna, Sparkles, AlertTriangle
} from "lucide-react";

const CareerTwin = () => {
  const { token, user } = useContext(AuthContext);
  const [twinProfile, setTwinProfile] = useState(null);
  const [careerScore, setCareerScore] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("chat");
  const [insights, setInsights] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAvatarCustomizer, setShowAvatarCustomizer] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (token) {
      fetchTwinProfile();
      fetchConversations();
      fetchInsights();
      fetchRecommendations();
    }
  }, [token]);

  const fetchTwinProfile = async () => {
    try {
      const response = await axios.get(`${API}/career-twin/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwinProfile(response.data.twin);
      // Use DNA profile instead of career score
      setCareerScore(response.data.dna_profile || {
        dna_strength_score: 0,
        skill_genes_count: 0,
        skill_mutations_count: 0,
        completion_percentage: 0
      });
      
      // Check if this is first time (no interactions)
      if (response.data.twin.total_interactions === 0) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error("Error fetching twin profile:", error);
      toast.error("Failed to load career twin");
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/career-twin/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.conversations || []);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/career-twin/insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(response.data.insights || []);
    } catch (error) {
      console.error("Error fetching insights:", error);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/career-twin/recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date().toISOString(),
    };

    setMessages([...messages, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/career-twin/chat`,
        { message: input },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: response.data.timestamp,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      
      // Refresh profile to update interaction count
      fetchTwinProfile();
    } catch (error) {
      toast.error("Failed to get response");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I'm having trouble responding right now. Please try again.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const markInsightAsRead = async (insightId) => {
    try {
      await axios.put(
        `${API}/career-twin/insights/${insightId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchInsights();
      fetchTwinProfile();
    } catch (error) {
      console.error("Error marking insight as read:", error);
    }
  };

  const handleSaveAvatar = async (avatarConfig) => {
    setSavingAvatar(true);
    try {
      await axios.put(
        `${API}/career-twin/avatar`,
        { avatar_config: avatarConfig },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Avatar customization saved!");
      setShowAvatarCustomizer(false);
      // Refresh twin profile to get updated avatar
      fetchTwinProfile();
    } catch (error) {
      console.error("Error saving avatar:", error);
      toast.error("Failed to save avatar customization");
    } finally {
      setSavingAvatar(false);
    }
  };

  const generateAvatarUrl = (config) => {
    if (!config || typeof config !== 'object' || !config.top) {
      // Return default avatar URL based on twin name
      return `https://api.dicebear.com/7.x/avataaars/svg?seed=${twinProfile?.twin_name || 'CareerTwin'}&backgroundColor=b6e3f4`;
    }
    
    const params = new URLSearchParams();
    params.append("seed", twinProfile?.twin_name || "CareerTwin");
    
    // Only append parameters that have values
    if (config.top) params.append("top", config.top);
    if (config.hairColor) params.append("hairColor", config.hairColor);
    if (config.accessories) params.append("accessories", config.accessories);
    if (config.facialHair) params.append("facialHair", config.facialHair);
    if (config.clothing) params.append("clothing", config.clothing);
    if (config.clothesColor) params.append("clothesColor", config.clothesColor);
    if (config.skinColor) params.append("skinColor", config.skinColor);
    if (config.eyes) params.append("eyes", config.eyes);
    if (config.eyebrows) params.append("eyebrows", config.eyebrows);
    if (config.mouth) params.append("mouth", config.mouth);
    if (config.backgroundColor) params.append("backgroundColor", config.backgroundColor);
    
    return `https://api.dicebear.com/7.x/avataaars/svg?${params.toString()}`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return "bg-green-100";
    if (score >= 60) return "bg-yellow-100";
    return "bg-red-100";
  };

  if (!twinProfile || !careerScore) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Bot className="w-16 h-16 mx-auto text-indigo-500 animate-pulse" />
            <p className="mt-4 text-gray-600">Loading your Career Twin...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Bot className="w-10 h-10 text-indigo-500" />
            My Career Twin
          </h1>
          <p className="text-gray-600 text-lg">
            Your personal AI career companion that learns and grows with you
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Twin Profile */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardContent className="p-6">
                {/* Avatar */}
                <div className="text-center mb-4">
                  <div 
                    className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden flex items-center justify-center shadow-lg border-4 border-white"
                    style={{ 
                      backgroundColor: twinProfile.avatar_config?.backgroundColor 
                        ? `#${twinProfile.avatar_config.backgroundColor}` 
                        : '#b6e3f4' 
                    }}
                  >
                    <img 
                      src={generateAvatarUrl(twinProfile.avatar_config)}
                      alt="Career Twin Avatar"
                      className="w-full h-full"
                      data-testid="career-twin-avatar"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">{twinProfile.twin_name}</h3>
                  <p className="text-sm text-gray-500 capitalize">{twinProfile.personality_type} Companion</p>
                </div>

                {/* DNA Strength Score */}
                <div className={`mb-4 p-4 rounded-lg ${getScoreBgColor(careerScore.dna_strength_score || 0)}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Dna className="w-4 h-4" />
                      DNA Strength
                    </span>
                    <span className={`text-2xl font-bold ${getScoreColor(careerScore.dna_strength_score || 0)}`}>
                      {careerScore.dna_strength_score || 0}
                    </span>
                  </div>
                  <Progress value={careerScore.dna_strength_score || 0} className="h-2" />
                  <p className="text-xs text-gray-600 mt-1">
                    {careerScore.completion_percentage || 0}% profile complete
                  </p>
                </div>

                {/* Stats */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> Courses
                    </span>
                    <span className="font-semibold">{twinProfile.statistics?.total_courses || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-2">
                      <Award className="w-4 h-4" /> Completed
                    </span>
                    <span className="font-semibold">{twinProfile.statistics?.completed_courses || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Interactions
                    </span>
                    <span className="font-semibold">{twinProfile.total_interactions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-2">
                      <Bell className="w-4 h-4" /> New Insights
                    </span>
                    <Badge variant="secondary">{twinProfile.unread_insights || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" /> Skill Genes
                    </span>
                    <Badge variant="default" className="bg-green-500">{careerScore.skill_genes_count || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Mutations
                    </span>
                    <Badge variant="default" className="bg-orange-500">{careerScore.skill_mutations_count || 0}</Badge>
                  </div>
                </div>

                {/* View Full DNA Button */}
                <Button
                  variant="outline"
                  className="w-full mb-4"
                  size="sm"
                  onClick={() => window.location.href = '/skill-dna'}
                >
                  <Dna className="w-4 h-4 mr-2" />
                  View Full DNA Profile
                </Button>

                {/* Customize Button */}
                <Button 
                  variant="outline" 
                  className="w-full" 
                  size="sm"
                  onClick={() => setShowAvatarCustomizer(true)}
                  data-testid="customize-twin-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Customize Twin
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Avatar Customizer Modal */}
          <AvatarCustomizer
            isOpen={showAvatarCustomizer}
            onClose={() => setShowAvatarCustomizer(false)}
            currentAvatar={twinProfile.avatar_config}
            twinName={twinProfile.twin_name}
            onSave={handleSaveAvatar}
            loading={savingAvatar}
          />

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto gap-1 p-1">
                <TabsTrigger value="chat" className="text-xs sm:text-sm py-2 px-2 sm:px-4">
                  <MessageSquare className="w-4 h-4 sm:mr-2 mr-1" />
                  <span className="hidden xs:inline">Chat</span>
                  <span className="xs:hidden">Chat</span>
                </TabsTrigger>
                <TabsTrigger value="insights" className="text-xs sm:text-sm py-2 px-2 sm:px-4">
                  <TrendingUp className="w-4 h-4 sm:mr-2 mr-1" />
                  <span>Insights</span>
                </TabsTrigger>
                <TabsTrigger value="score" className="text-xs sm:text-sm py-2 px-2 sm:px-4">
                  <Dna className="w-4 h-4 sm:mr-2 mr-1" />
                  <span className="hidden sm:inline">DNA Profile</span>
                  <span className="sm:hidden">DNA</span>
                </TabsTrigger>
                <TabsTrigger value="recommendations" className="text-xs sm:text-sm py-2 px-2 sm:px-4">
                  <Zap className="w-4 h-4 sm:mr-2 mr-1" />
                  <span className="hidden sm:inline">Recommendations</span>
                  <span className="sm:hidden">Recs</span>
                </TabsTrigger>
              </TabsList>

              {/* Chat Tab */}
              <TabsContent value="chat">
                <Card className="h-[600px] flex flex-col">
                  <CardHeader className="border-b">
                    <CardTitle className="flex items-center gap-2">
                      <Bot className="w-6 h-6 text-indigo-500" />
                      Chat with {twinProfile.twin_name}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="flex-1 overflow-y-auto p-6 space-y-4">
                    {messages.length === 0 && (
                      <div className="text-center py-12">
                        <Bot className="w-16 h-16 mx-auto text-indigo-300 mb-4" />
                        <p className="text-gray-500">
                          Start a conversation with your Career Twin! Ask about your progress, career goals, or get personalized recommendations.
                        </p>
                      </div>
                    )}
                    
                    {messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex gap-3 ${
                          msg.role === "user" ? "justify-end" : "justify-start"
                        }`}
                      >
                        {msg.role === "assistant" && (
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-5 h-5 text-indigo-600" />
                          </div>
                        )}
                        <div
                          className={`max-w-[80%] rounded-lg p-4 ${
                            msg.role === "user"
                              ? "bg-indigo-600 text-white"
                              : "bg-white border border-gray-200"
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                          <p className={`text-xs mt-2 ${msg.role === "user" ? "text-indigo-200" : "text-gray-400"}`}>
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                        {msg.role === "user" && (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-gray-600" />
                          </div>
                        )}
                      </div>
                    ))}
                    {loading && (
                      <div className="flex gap-3 justify-start">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                          <Bot className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </CardContent>

                  <div className="border-t p-4">
                    <div className="flex gap-2">
                      <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask your Career Twin anything..."
                        className="flex-1"
                        disabled={loading}
                      />
                      <Button onClick={handleSend} disabled={loading || !input.trim()}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* Insights Tab */}
              <TabsContent value="insights">
                <div className="space-y-4">
                  {insights.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No insights yet. Keep learning and your twin will generate personalized insights!</p>
                      </CardContent>
                    </Card>
                  ) : (
                    insights.map((insight) => (
                      <Card key={insight.id} className={insight.is_read ? "opacity-75" : ""}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant={
                                  insight.priority === "high" ? "destructive" :
                                  insight.priority === "medium" ? "default" : "secondary"
                                }>
                                  {insight.priority}
                                </Badge>
                                <Badge variant="outline">{insight.insight_type.replace("_", " ")}</Badge>
                              </div>
                              <CardTitle className="text-xl">{insight.title}</CardTitle>
                            </div>
                            {!insight.is_read && (
                              <Badge className="bg-indigo-100 text-indigo-700">New</Badge>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-700 mb-4">{insight.content}</p>
                          {insight.action_items && insight.action_items.length > 0 && (
                            <div className="bg-indigo-50 p-4 rounded-lg">
                              <p className="font-semibold text-sm text-indigo-900 mb-2">Action Items:</p>
                              <ul className="space-y-1">
                                {insight.action_items.map((item, index) => (
                                  <li key={index} className="text-sm text-indigo-700 flex items-center gap-2">
                                    <ChevronRight className="w-4 h-4" />
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {!insight.is_read && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="mt-4"
                              onClick={() => markInsightAsRead(insight.id)}
                            >
                              Mark as Read
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* DNA Profile Tab */}
              <TabsContent value="score">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Dna className="w-6 h-6 text-indigo-600" />
                      Skill DNA Profile Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-lg font-medium">DNA Strength Score</span>
                        <span className={`text-4xl font-bold ${getScoreColor(careerScore.dna_strength_score || 0)}`}>
                          {careerScore.dna_strength_score || 0}/100
                        </span>
                      </div>
                      <Progress value={careerScore.dna_strength_score || 0} className="h-3" />
                      <p className="text-sm text-gray-600 mt-2">
                        {careerScore.completion_percentage || 0}% profile complete
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <Card className="border-2 border-green-200 bg-green-50">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600 mb-1">Skill Genes</p>
                              <p className="text-3xl font-bold text-green-700">
                                {careerScore.skill_genes_count || 0}
                              </p>
                              <p className="text-xs text-gray-600">Natural Strengths</p>
                            </div>
                            <Sparkles className="w-12 h-12 text-green-500" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-2 border-orange-200 bg-orange-50">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600 mb-1">Skill Mutations</p>
                              <p className="text-3xl font-bold text-orange-700">
                                {careerScore.skill_mutations_count || 0}
                              </p>
                              <p className="text-xs text-gray-600">Growth Areas</p>
                            </div>
                            <AlertTriangle className="w-12 h-12 text-orange-500" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {careerScore.top_career_match && (
                      <Card className="border-2 border-indigo-200 bg-indigo-50">
                        <CardContent className="pt-6">
                          <p className="text-sm text-gray-600 mb-2">Top Career Match</p>
                          <p className="text-xl font-bold text-indigo-900 mb-2">
                            {careerScore.top_career_match.career_title}
                          </p>
                          <div className="flex items-center gap-2">
                            <Progress value={careerScore.top_career_match.compatibility_score} className="flex-1" />
                            <span className="text-lg font-bold text-indigo-700">
                              {careerScore.top_career_match.compatibility_score}%
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-2">
                            {careerScore.top_career_match.time_to_ready}
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    <div className="mt-6">
                      <Button
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                        size="lg"
                        onClick={() => window.location.href = '/skill-dna'}
                      >
                        <Dna className="w-5 h-5 mr-2" />
                        View Complete DNA Analysis
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Recommendations Tab */}
              <TabsContent value="recommendations">
                <div className="space-y-4">
                  {recommendations.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Zap className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No recommendations yet. Complete your profile and take assessments to get personalized recommendations!</p>
                      </CardContent>
                    </Card>
                  ) : (
                    recommendations.map((rec) => (
                      <Card key={rec.id}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <Badge className="mb-2">{rec.type}</Badge>
                              <CardTitle className="text-xl">{rec.title}</CardTitle>
                            </div>
                            <div className="flex">
                              {[...Array(rec.priority)].map((_, i) => (
                                <span key={i} className="text-yellow-400">⭐</span>
                              ))}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-700 mb-3">{rec.description}</p>
                          <div className="bg-indigo-50 p-3 rounded-lg mb-4">
                            <p className="text-sm text-indigo-900">
                              <strong>Why this is recommended:</strong> {rec.reason}
                            </p>
                          </div>
                          {rec.url && (
                            <Button>
                              View Details
                              <ChevronRight className="w-4 h-4 ml-2" />
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CareerTwin;
